/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";
import {
  ISuperficialAddressProps,
} from "interfaces/Address";
import { useOccurrenceRegister } from "contexts/register/OccurrenceContext";
import { ISuperficialClient } from "interfaces/SubClient";

import { ReactComponent as CheckIcon } from "assets/check.svg";

import * as Checkbox from "@radix-ui/react-checkbox";

import { OccurrenceToggleButton } from "../../../OccurrenceToggleButton";

import { IPoliceman } from "interfaces/Policeman";
import { IClient } from "interfaces/Client";
import { IOccurrence } from "interfaces/Occurrence";
import { ISuperficialIVehicle } from "interfaces/Vehicle";
import { ErrorMsg } from "../../../ErrorMsg";
import { useAuth } from "../../../../contexts/authContext";
import { treatSocketLocations } from "../../../../utils/treatSocketLocations";
import { socket } from "hooks/useSocket";
import { DivAlert } from "components/display/DivAlert";

import * as S from "./styles";
import { InputMap } from "components/InputMap";
import { AutoComplete } from "components/Autocomplete";
import { useDataList } from "hooks/useDataList";

type workType = "Policial" | "Assistente";

type Coords = {
  latitude: number;
  longitude: number;
};

type PolicemanSelectOption = {
  value: IPoliceman
  label: string
}

export type MarkerType =
  | "policeman"
  | "occVehicle"
  | "occPatrimony"
  | "store"
  | "policemanOnDuty";

type Markers = {
  id: string;
  coords: Coords;
  type: MarkerType;
  name?: string;
  working?: boolean;
};

type SocketMarkers = {
  [id: string]: SocketMarkerBody;
};

export type SocketMarkerBody = {
  coords: Coords;
  token: string;
  working: boolean;
  name?: string;
};

type IOccurrenceProps = {
  workType: workType;
  setWorkType: (value: workType) => void;
  patrimonialType: number;
  setPatrimonialType: (number: number) => void;
  occurrenceData?: IOccurrence;
  hasNumber?: boolean;
};
function OccurrenceForm({
  patrimonialType,
  setPatrimonialType,
  occurrenceData,
  hasNumber,
}: IOccurrenceProps) {
  const {
    occurrence,
    setOccurrence,
    addError,
    removeError,
    errors,
    vehicle,
    setVehicle,
    filterClient,
    setFilterClient,
    filterSubClient,
    setFilterSubClient,
    isManual,
    setIsManual,
    isSubclientAddressRegister,
    setIsSubclientAddressRegister,
    setSelectedPolicemen,
  } = useOccurrenceRegister();

  const { tenant } = useAuth();

  const [socketPolicemen, setSocketPolicemen] = useState<Markers[]>([]);
  const [onlinePolicemen, setOnlinePolicemen] = useState<IPoliceman[]>([]);
  const [selectedSubClient, setSelectedSubClient] =
    useState<ISuperficialClient>();

  const [isSubclientWithoutAddress, setIsSubclientWithoutAddress] =
    useState<boolean>(false);

  const clientsData = useDataList<IClient>({
    queryKey: 'listAllClients',
    route: 'clients',
    filter: {
      notPage: true,
    }
  }).data;

  const policemenData = useDataList<IPoliceman>({
    queryKey: 'listAllPoliceman',
    route: 'policemen',
    filter: {
      relations: {
        user: true,
      },
      notPage: true,
    }
  }).data;

  const subClientsData = useDataList<ISuperficialClient>({
    queryKey: 'listAllSuperficialClients',
    route: 'superficial/clients',
    filter: {
      params: { client: { id: filterClient.id } },
      relations: {
        superficialAddress: true,
      },
      notPage: true,
    }
  }).data;

  const vehicleData = useDataList<ISuperficialIVehicle>({
    queryKey: 'listAllSuperficialVehicles',
    route: 'superficial/vehicles',
    filter: {
      notPage: true,
    }
  }).data;

  useEffect(() => {
    if (tenant?.id) {
      socket.emit("listLocations", tenant.id);
      socket.on("listLocations", onLocationChange);
    }

    return () => {
      socket.off("listLocations");
    };
  }, [policemenData]);

  useEffect(() => {
    const online: IPoliceman[] = [];
    for (let marker of socketPolicemen) {
      if (policemenData && policemenData.items)
        for (let policeman of policemenData.items) {
          if (policeman.id === marker.id && !marker.working) {
            online.push(policeman);
          }
        }
    }
    setOnlinePolicemen(online);
  }, [socketPolicemen]);

  useEffect(() => {
    if (occurrenceData) {
      setFilterClient(occurrenceData.client);
    }
  }, [occurrence]);

  function onLocationChange(locations: SocketMarkers) {
    const _locations = treatSocketLocations({
      socketLocations: locations,
      tenantId: tenant?.id ?? "",
    });

    const onlinePolicemen = [];

    for (let policeman of _locations) {
      if (!policeman.working) {
        onlinePolicemen.push(policeman);
      }
    }

    setSocketPolicemen([...onlinePolicemen]);
  }

  function formatAddress() {
    const address = selectedSubClient?.superficialAddress;
    if (!address || !address.coordX || !address.coordY) return undefined;

    return {
      lat: address.coordX,
      lng: address.coordY,
      formattedAddress: `${address.street ?? "--"}, ${address.number ?? "--"
        }, ${address.city ?? "--"}, ${address.uf ?? "--"}`,
    };
  }

  const [coordY, setCoordY] = useState(0);
  const [coordX, setCoordX] = useState(0);

  const [address, setAddress] = useState<ISuperficialAddressProps>(
    {} as ISuperficialAddressProps
  );

  useEffect(() => {
    setOccurrence((value) => {
      value.coordX = selectedSubClient?.superficialAddress?.coordX
        ? selectedSubClient?.superficialAddress?.coordX
        : coordX !== 0
          ? coordX
          : occurrence.coordX;
      value.coordY = selectedSubClient?.superficialAddress?.coordY
        ? selectedSubClient?.superficialAddress?.coordY
        : coordY !== 0
          ? coordY
          : occurrence.coordY;
      value.city = address.city ? address.city : occurrence.city;
      value.district = address.district ? address.district : occurrence.city;
      value.number = address.number ? address.number : occurrence.number;
      if (address.postalCode && address.postalCode.length === 5) {
        value.postalCode = address.postalCode + "-000";
      } else {
        value.postalCode = address.postalCode;
      }
      value.street = address.street ? address.street : occurrence.street;
      value.uf = address.uf ? address.uf : occurrence.uf;
      value.superficialAddressId = address.id;
      return value;
    });
  }, [address]);

  const policemanSelectOptions = onlinePolicemen.map((policemanOnline) => ({
    label: policemanOnline.user.name,
    value: policemanOnline
  }))

  return (
    <S.Wrapper>
      {!occurrenceData && (
        <S.Header>
          <OccurrenceToggleButton
            isEstablishment={!isManual}
            label="Selecione o tipo de gerenciamento"
            leftLabel="Mobile"
            rightLabel="Manual (dashboard)"
            leftClick={() => {
              setIsManual(false);
            }}
            response
            rightClick={() => {
              setIsManual(true);
            }}
          />

          <OccurrenceToggleButton
            isEstablishment={patrimonialType === 1}
            label="Selecione o tipo do serviço"
            leftLabel="Patrimonial"
            rightLabel="Veículo"
            leftClick={() => {
              setPatrimonialType(1);
            }}
            response
            rightClick={() => {
              setPatrimonialType(2);
            }}
          />
        </S.Header>
      )}

      <S.Form>
        <S.Column>
          <S.ClientDataContainer>

            {!occurrenceData && (
              <AutoComplete
                id="model"
                label="Selecione um ou mais prs online para essa ocorrencia - (opcional)"
                placeholder="Selecione um ou mais prs online"
                options={policemanSelectOptions}
                isMulti
                onChange={(selected) => {
                  const policemanArray = (selected as Array<PolicemanSelectOption>).map((option) => option.value)
                  setSelectedPolicemen(policemanArray)
                }}
              />)}

            {clientsData && (
              <AutoComplete
                id="client"
                defaultValue={filterClient.id ? filterClient : null}
                label="Selecione um cliente"
                isMulti={false}
                isSearchable
                placeholder="Selecione um cliente"
                noOptionsMessage={() => 'Nenhum cliente encontrado'}
                options={clientsData.items}
                getOptionLabel={(option) => option.name}
                error={errors['noClientSelected']}
                onChange={(selected) => {
                  if (!selected) {
                    setFilterClient({} as IClient);
                  }

                  if (selected) {
                    setFilterClient(selected as IClient);
                  }
                }}
                onBlur={() => {
                  if (!filterClient.id) {
                    addError("noClientSelected", "Necessario selecionar um cliente!")
                  } else {
                    removeError("noClientSelected")
                  }
                }}
              />
            )}

            {subClientsData && patrimonialType === 1 && (
              <AutoComplete
                label="Selecione um subcliente"
                placeholder="Selecione um subcliente"
                id="subclient"
                isMulti={false}
                noOptionsMessage={() => 'Subcliente não encontrado ou cliente não possui subclientes.'}
                getOptionLabel={(option) => option.name}
                isSearchable
                options={filterClient ? subClientsData.items : []}
                defaultValue={
                  occurrenceData?.occurrencesSuperficialAddresses
                    .superficialAddress.superficialClient?.name ||
                  occurrence.superficialClient?.name
                }
                onChange={(selected) => {
                  setFilterSubClient(selected.id);
                  setSelectedSubClient(selected);

                  if (!selected.superficialAddress) {
                    return setIsSubclientWithoutAddress(true);
                  }

                  setIsSubclientWithoutAddress(false);

                  setOccurrence((value) => {
                    value.superficialClient = selected;
                    return value;
                  });
                  setAddress(selected.superficialAddress);
                }}
                onBlur={() => {
                  if (!selectedSubClient?.id) {
                    addError("noSubclientSelected", "Necessario selecionar um subcliente!")
                  } else {
                    removeError("noSubclientSelected")
                  }
                }}
                error={
                  errors['noSubclientSelected'] ??
                  (errors.superficialClientsIds &&
                    "Nenhum subcliente foi selecionado")
                }
              />
            )}
          </S.ClientDataContainer>

          {isSubclientWithoutAddress && (
            <DivAlert
              title="Aviso"
              text="
                O sub-cliente selecionado não possui um endereço registrado. 
              "
            >
              <S.AlertCheckboxContainer>
                <Checkbox.Root
                  className="checkbox-root"
                  id="address_association_cb"
                  checked={isSubclientAddressRegister}
                  onClick={() => {
                    setIsSubclientAddressRegister(!isSubclientAddressRegister);
                  }}
                >
                  <Checkbox.Indicator className="checkbox-indicator">
                    <CheckIcon />
                  </Checkbox.Indicator>
                </Checkbox.Root>
                <S.CheckboxLabel htmlFor="address_association_cb">
                  Registrar o endereço dessa ocorrência como o endereço desse
                  sub-cliente
                </S.CheckboxLabel>
              </S.AlertCheckboxContainer>
            </DivAlert>
          )}

          {patrimonialType === 2 && vehicleData && filterSubClient && (
            <AutoComplete
              id="model"
              defaultValue={
                occurrenceData?.superficialVehicle || vehicle
              }
              label="Caso já cadastrado o veículo, selecione - (Opcional)"
              options={vehicleData.items}
              getOptionLabel={(option) => option.model}
              onChange={(selected) => {
                setVehicle(selected as ISuperficialIVehicle);
              }}
            />
          )}
        </S.Column>
        <S.Column>
          {!occurrenceData && (
            <S.Map>
              <label>Endereço</label>

              <InputMap
                label={
                  patrimonialType === 1 && !isSubclientWithoutAddress
                    ? "(Será coletado pelo registro do subcliente)"
                    : undefined
                }
                location={formatAddress()}
                onCoordsChange={(coords) => {
                  setCoordX(coords.lat);
                  setCoordY(coords.lng);
                }}
                onAddressChange={(address) => {
                  setAddress(address);
                }}
                readonly={patrimonialType === 1 && !isSubclientWithoutAddress}
                error={errors.CoordX}
              />

              {hasNumber && <ErrorMsg text="O número é obrigatório" />}
            </S.Map>

          )}

        </S.Column>
      </S.Form>
    </S.Wrapper>
  );
}

export { OccurrenceForm };
